// ============================================================================
// SAP CMMS CONNECTOR ENGINE
// Optimal® Availability Studio — SAP Plant Maintenance Integration
// Supports: SAP PM Equipment, Functional Locations, Notifications,
//           Maintenance Orders, Task Lists, BOMs, and Failure History
// Formats:  IDoc (XML), CSV, RFC simulation (JSON), SAP standard tables
// ============================================================================

/**
 * SAP PM Table Mappings — maps SAP standard tables to OptimalAvailability entities
 */
const SAP_TABLE_MAP = {
  // Equipment Master
  EQUI: {
    entity: 'equipments',
    fields: {
      EQUNR: { target: 'id', label: 'Equipment Number' },
      TXTMI: { target: 'name', label: 'Description (Short)' },
      EQKTX: { target: 'description', label: 'Description (Long)' },
      EQTYP: { target: 'type', label: 'Equipment Category' },
      TPLNR: { target: 'location', label: 'Functional Location' },
      SWERK: { target: 'plantCode', label: 'Maintenance Plant' },
      GEWRK: { target: 'workCenter', label: 'Main Work Center' },
      HERST: { target: 'manufacturer', label: 'Manufacturer' },
      TYPBZ: { target: 'model', label: 'Model Number' },
      SERGE: { target: 'serialNumber', label: 'Serial Number' },
      BAESSION: { target: 'installDate', label: 'Start-up Date' },
      INBDT: { target: 'commissionDate', label: 'Commissioning Date' },
      STATTEXT: { target: 'state', label: 'System Status' },
      KOSTL: { target: 'costCenter', label: 'Cost Center' },
      GROES: { target: 'capacity', label: 'Size/Dimension' },
      BRGEW: { target: 'weight', label: 'Weight' },
      ANSWT: { target: 'acquisitionValue', label: 'Acquisition Value' }
    }
  },

  // Functional Location
  IFLO: {
    entity: 'locations',
    fields: {
      TPLNR: { target: 'id', label: 'Functional Location' },
      PLTXT: { target: 'name', label: 'Description' },
      FLTYP: { target: 'type', label: 'Location Category' },
      SWERK: { target: 'plantCode', label: 'Maintenance Plant' },
      BEBER: { target: 'plantSection', label: 'Plant Section' },
      MSGRP: { target: 'maintenanceGroup', label: 'Maintenance Group' },
      TPLMA: { target: 'parentLocation', label: 'Superior Func. Location' },
      IWERK: { target: 'planningPlant', label: 'Planning Plant' },
      GEWRK: { target: 'workCenter', label: 'Main Work Center' },
      KOSTL: { target: 'costCenter', label: 'Cost Center' }
    }
  },

  // Maintenance Notification (Failure data)
  QMEL: {
    entity: 'failures',
    fields: {
      QMNUM: { target: 'id', label: 'Notification Number' },
      QMART: { target: 'notificationType', label: 'Notification Type' },
      QMTXT: { target: 'description', label: 'Short Text' },
      EQUNR: { target: 'equipmentId', label: 'Equipment Number' },
      TPLNR: { target: 'locationId', label: 'Functional Location' },
      AUSVN: { target: 'breakdownStart', label: 'Malfunction Start' },
      AUZTV: { target: 'breakdownStartTime', label: 'Malfunction Start Time' },
      AUSBS: { target: 'breakdownEnd', label: 'Malfunction End' },
      AUZTB: { target: 'breakdownEndTime', label: 'Malfunction End Time' },
      AUSZT: { target: 'downtime', label: 'Breakdown Duration (hours)' },
      PRIESSION: { target: 'priority', label: 'Priority' },
      ERDAT: { target: 'createdDate', label: 'Created On' },
      ERNAM: { target: 'createdBy', label: 'Created By' },
      STRMN: { target: 'requiredStart', label: 'Required Start Date' },
      LTRMN: { target: 'requiredEnd', label: 'Required End Date' },
      FEGRP: { target: 'failureCodeGroup', label: 'Code Group (Object Part)' },
      FECOD: { target: 'failureCode', label: 'Code (Object Part)' },
      OTGRP: { target: 'causeCodeGroup', label: 'Code Group (Cause)' },
      OTEIL: { target: 'causeCode', label: 'Code (Cause)' },
      UESSION: { target: 'damageCodeGroup', label: 'Code Group (Damage)' },
      URGRP: { target: 'damageCode', label: 'Code (Damage)' }
    }
  },

  // Maintenance Order
  AUFK: {
    entity: 'maintenanceorders',
    fields: {
      AUFNR: { target: 'id', label: 'Order Number' },
      AUART: { target: 'orderType', label: 'Order Type' },
      KTEXT: { target: 'description', label: 'Short Text' },
      EQUNR: { target: 'equipmentId', label: 'Equipment Number' },
      TPLNR: { target: 'locationId', label: 'Functional Location' },
      GSTRP: { target: 'scheduledStart', label: 'Basic Start Date' },
      GLTRP: { target: 'scheduledEnd', label: 'Basic Finish Date' },
      GETRI: { target: 'actualStart', label: 'Actual Start Date' },
      GLTRI: { target: 'actualEnd', label: 'Actual Finish Date' },
      KOSTL: { target: 'costCenter', label: 'Cost Center' },
      ARBEI: { target: 'plannedWork', label: 'Planned Work (hours)' },
      ISMNW: { target: 'actualWork', label: 'Actual Work (hours)' },
      PRIOK: { target: 'priority', label: 'Priority' }
    }
  },

  // Maintenance Task List (Operations)
  PLKO: {
    entity: 'tasklists',
    fields: {
      PLNTY: { target: 'taskListType', label: 'Task List Type' },
      PLNNR: { target: 'id', label: 'Group Number' },
      PLNAL: { target: 'counter', label: 'Group Counter' },
      KTEXT: { target: 'description', label: 'Description' },
      WERKS: { target: 'plantCode', label: 'Plant' },
      VERWE: { target: 'usage', label: 'Task List Usage' },
      STATU: { target: 'status', label: 'Status' },
      VAESSION: { target: 'validFrom', label: 'Valid From' },
      DELKZ: { target: 'deleteFlag', label: 'Deletion Flag' }
    }
  },

  // Maintenance Plan
  MPLA: {
    entity: 'maintenanceplans',
    fields: {
      WARPL: { target: 'id', label: 'Maintenance Plan Number' },
      WPTEXT: { target: 'description', label: 'Description' },
      STRAT: { target: 'strategy', label: 'Maintenance Strategy' },
      AESSION: { target: 'planCategory', label: 'Plan Category' },
      BEGDT: { target: 'startDate', label: 'Start of Cycle' },
      ESSION: { target: 'callHorizon', label: 'Call Horizon %' },
      CYCLT: { target: 'cycleLength', label: 'Cycle (days)' },
      EINESSION: { target: 'unit', label: 'Unit of Cycle' }
    }
  },

  // BOM (Bill of Materials for equipment)
  STPO: {
    entity: 'spares',
    fields: {
      STLNR: { target: 'bomId', label: 'BOM Number' },
      POSNR: { target: 'itemNumber', label: 'Item Number' },
      IDNRK: { target: 'id', label: 'Material Number' },
      MENGE: { target: 'quantity', label: 'Quantity' },
      MEINS: { target: 'unit', label: 'Unit of Measure' },
      POTX1: { target: 'name', label: 'Item Text' },
      POSTP: { target: 'itemCategory', label: 'Item Category' }
    }
  },

  // Document Information Records (Documents)
  DOKAR: {
    entity: 'documents',
    fields: {
      DOKNR: { target: 'docNumber', label: 'Document Number' },
      DOKTL: { target: 'docType', label: 'Document Type' },
      DOKVR: { target: 'docPart', label: 'Document Part' },
      DOKBA: { target: 'docVersion', label: 'Document Version' },
      DKTXT: { target: 'description', label: 'Description' },
      DOSTAT: { target: 'statusDoc', label: 'Document Status' }
    }
  },

  // Work Centers / Labour (CRHD)
  CRHD: {
    entity: 'labor',
    fields: {
      ARBPL: { target: 'workCenter', label: 'Work Center' },
      KTEXT: { target: 'description', label: 'Description' },
      ARBTY: { target: 'laborType', label: 'Labor Type' },
      KOSTL: { target: 'costCenter', label: 'Cost Center' },
      LSTAR: { target: 'costRate', label: 'Cost Rate' },
      WAERS: { target: 'currency', label: 'Currency' },
      KAPAZ: { target: 'capacity', label: 'Capacity' }
    }
  }
};

/**
 * SAP Connection Configuration
 */
const DEFAULT_SAP_CONFIG = {
  connectionType: 'file', // 'file' | 'rfc' | 'odata'
  // RFC connection (requires SAP RFC SDK / JCo)
  rfc: {
    host: '',
    systemNumber: '00',
    client: '100',
    user: '',
    password: '',
    language: 'EN',
    poolSize: 5
  },
  // OData connection (SAP S/4HANA)
  odata: {
    baseUrl: '',
    apiKey: '',
    authType: 'basic' // 'basic' | 'oauth2' | 'certificate'
  },
  // File-based import/export
  file: {
    format: 'csv', // 'csv' | 'idoc' | 'json'
    delimiter: ',',
    encoding: 'utf-8',
    dateFormat: 'YYYYMMDD'
  },
  // Sync settings
  sync: {
    autoSync: false,
    interval: 3600, // seconds
    lastSync: null,
    syncDirection: 'bidirectional', // 'import' | 'export' | 'bidirectional'
    conflictResolution: 'sap-wins' // 'sap-wins' | 'local-wins' | 'manual'
  },
  // Field mapping overrides
  mappingOverrides: {}
};

/**
 * SAP Connector Engine
 */
class SAPConnector {
  constructor(config = {}) {
    this.config = { ...DEFAULT_SAP_CONFIG, ...config };
    this.syncLog = [];
    this.fieldMappings = { ...SAP_TABLE_MAP };
  }

  // ========== CONNECTION MANAGEMENT ==========

  /**
   * Test SAP connection
   */
  async testConnection() {
    const { connectionType } = this.config;

    if (connectionType === 'file') {
      return { success: true, message: 'File-based mode — no connection required', mode: 'file' };
    }

    if (connectionType === 'rfc') {
      const { host, systemNumber, client } = this.config.rfc;
      if (!host) return { success: false, message: 'RFC host not configured' };
      // In production, this would call SAP RFC SDK
      return {
        success: true,
        message: `RFC connection configured: ${host} (SysNr: ${systemNumber}, Client: ${client})`,
        mode: 'rfc',
        note: 'RFC requires SAP JCo/NW RFC SDK — use File mode for standalone operation'
      };
    }

    if (connectionType === 'odata') {
      const { baseUrl } = this.config.odata;
      if (!baseUrl) return { success: false, message: 'OData base URL not configured' };
      return {
        success: true,
        message: `OData endpoint configured: ${baseUrl}`,
        mode: 'odata'
      };
    }

    return { success: false, message: 'Unknown connection type' };
  }

  /**
   * Get available SAP tables for the current configuration
   */
  getAvailableTables() {
    return Object.entries(SAP_TABLE_MAP).map(([table, config]) => ({
      table,
      entity: config.entity,
      fieldCount: Object.keys(config.fields).length,
      fields: Object.entries(config.fields).map(([sapField, mapping]) => ({
        sapField,
        localField: mapping.target,
        label: mapping.label
      }))
    }));
  }

  // ========== IMPORT FROM SAP ==========

  /**
   * Import CSV data exported from SAP
   * @param {string} csvContent - Raw CSV content
   * @param {string} sapTable - SAP table name (EQUI, QMEL, etc.)
   * @returns {Object} Imported data mapped to local entities
   */
  importCSV(csvContent, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) {
      return { success: false, error: `Unknown SAP table: ${sapTable}`, records: [] };
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
      return { success: false, error: 'CSV file is empty or has no data rows', records: [] };
    }

    const delimiter = this.config.file.delimiter || ',';
    const headers = this._parseCSVLine(lines[0], delimiter);
    const records = [];
    const warnings = [];

    for (let i = 1; i < lines.length; i++) {
      const values = this._parseCSVLine(lines[i], delimiter);
      if (values.length === 0) continue;

      const record = {};
      let mapped = 0;

      headers.forEach((header, idx) => {
        const cleanHeader = header.trim().replace(/^"|"$/g, '');
        const fieldMapping = tableConfig.fields[cleanHeader];

        if (fieldMapping) {
          record[fieldMapping.target] = this._parseValue(values[idx], cleanHeader);
          mapped++;
        } else {
          // Try fuzzy match
          const fuzzyMatch = this._fuzzyMatchField(cleanHeader, tableConfig.fields);
          if (fuzzyMatch) {
            record[fuzzyMatch.target] = this._parseValue(values[idx], cleanHeader);
            mapped++;
            if (i === 1) {
              warnings.push(`Fuzzy matched: "${cleanHeader}" → ${fuzzyMatch.target}`);
            }
          } else {
            // Store as custom field
            record[`sap_${cleanHeader}`] = values[idx]?.trim();
          }
        }
      });

      if (mapped > 0 && record.id) {
        records.push(record);
      } else if (mapped > 0) {
        record.id = `SAP-${sapTable}-${String(i).padStart(4, '0')}`;
        records.push(record);
      }
    }

    this._logSync('import', sapTable, records.length, 'csv');

    return {
      success: true,
      table: sapTable,
      entity: tableConfig.entity,
      records,
      totalRows: lines.length - 1,
      mappedRows: records.length,
      warnings
    };
  }

  /**
   * Import from CSV using real parsing
   * @param {string} csvText - Raw CSV text content
   * @returns {Object} Import result with detected table and records
   */
  importFromCSV(csvText) {
    if (!csvText || typeof csvText !== 'string') {
      return { success: false, error: 'Invalid CSV content', records: [] };
    }

    const detectedTable = this._detectTableFromCSVHeaders(csvText.split('\n')[0]);
    if (!detectedTable) {
      return { success: false, error: 'Could not detect SAP table from CSV headers', records: [] };
    }

    return this.importCSV(csvText, detectedTable);
  }

  /**
   * Static helper to map a single SAP record using field definitions
   * @param {Object} record - Raw SAP record
   * @param {Object} tableMap - Table field mapping configuration
   * @returns {Object} Mapped record
   */
  static _mapSAPRecord(record, tableMap) {
    const mapped = {};
    if (tableMap && tableMap.fields) {
      Object.entries(tableMap.fields).forEach(([sapField, fieldDef]) => {
        if (record[sapField] !== undefined) {
          mapped[fieldDef.target] = record[sapField];
        }
      });
    }
    return Object.keys(mapped).length > 0 ? mapped : record;
  }

  /**
   * Import SAP IDoc XML format
   * @param {string} xmlContent - IDoc XML content
   * @returns {Object} Imported data
   */
  importIDoc(xmlContent) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlContent, 'text/xml');

    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      return { success: false, error: 'Invalid IDoc XML format', records: [] };
    }

    const results = {
      success: true,
      imports: [],
      totalRecords: 0,
      warnings: []
    };

    // Detect IDoc segments
    const idocNode = doc.querySelector('IDOC') || doc.documentElement;
    const segments = idocNode.children;

    for (const segment of segments) {
      const segmentName = segment.tagName;

      // Map IDoc segments to SAP tables
      const tableMapping = this._idocSegmentToTable(segmentName);
      if (!tableMapping) continue;

      const tableConfig = this.fieldMappings[tableMapping];
      if (!tableConfig) continue;

      const record = {};
      for (const field of segment.children) {
        const fieldName = field.tagName;
        const fieldValue = field.textContent?.trim();

        if (tableConfig.fields[fieldName]) {
          record[tableConfig.fields[fieldName].target] = this._parseValue(fieldValue, fieldName);
        }
      }

      if (Object.keys(record).length > 0) {
        if (!record.id) {
          record.id = `IDOC-${tableMapping}-${results.totalRecords + 1}`;
        }
        results.imports.push({
          table: tableMapping,
          entity: tableConfig.entity,
          record
        });
        results.totalRecords++;
      }
    }

    this._logSync('import', 'IDoc', results.totalRecords, 'idoc');
    return results;
  }

  /**
   * Import JSON data (SAP OData / API response format)
   * @param {Object} jsonData - Parsed JSON object
   * @param {string} sapTable - Target SAP table
   * @returns {Object} Imported data
   */
  importJSON(jsonData, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) {
      return { success: false, error: `Unknown SAP table: ${sapTable}`, records: [] };
    }

    // Handle OData response wrapper
    const dataArray = jsonData.d?.results || jsonData.value || jsonData.results || jsonData;
    if (!Array.isArray(dataArray)) {
      return { success: false, error: 'Expected array of records', records: [] };
    }

    const records = dataArray.map((item, idx) => {
      const record = {};
      Object.entries(tableConfig.fields).forEach(([sapField, mapping]) => {
        if (item[sapField] !== undefined) {
          record[mapping.target] = this._parseValue(item[sapField], sapField);
        }
      });
      if (!record.id) {
        record.id = `SAP-${sapTable}-${String(idx + 1).padStart(4, '0')}`;
      }
      return record;
    });

    this._logSync('import', sapTable, records.length, 'json');

    return {
      success: true,
      table: sapTable,
      entity: tableConfig.entity,
      records,
      totalRows: dataArray.length,
      mappedRows: records.length
    };
  }

  /**
   * Auto-detect file format and import
   * @param {string} content - File content
   * @param {string} filename - Original filename for format detection
   * @returns {Object} Import results
   */
  autoImport(content, filename) {
    const ext = filename?.split('.').pop()?.toLowerCase();

    // Try IDoc XML first
    if (ext === 'xml' || ext === 'idoc' || content.trim().startsWith('<')) {
      const result = this.importIDoc(content);
      if (result.success && result.totalRecords > 0) return result;
    }

    // Try JSON
    if (ext === 'json' || content.trim().startsWith('{') || content.trim().startsWith('[')) {
      try {
        const json = JSON.parse(content);
        // Detect which table based on field names
        const detectedTable = this._detectTableFromFields(
          Array.isArray(json) ? json[0] : json.d?.results?.[0] || json.value?.[0] || json
        );
        if (detectedTable) {
          return this.importJSON(json, detectedTable);
        }
      } catch (e) { /* not valid JSON */ }
    }

    // Try CSV — detect table from headers
    const firstLine = content.split('\n')[0];
    const detectedTable = this._detectTableFromCSVHeaders(firstLine);
    if (detectedTable) {
      return this.importCSV(content, detectedTable);
    }

    // Try all tables
    for (const table of Object.keys(this.fieldMappings)) {
      const result = this.importCSV(content, table);
      if (result.success && result.mappedRows > 0) return result;
    }

    return { success: false, error: 'Could not detect SAP data format or table type', records: [] };
  }

  // ========== EXPORT TO SAP ==========

  /**
   * Export local data to SAP CSV format
   * @param {Array} data - Local entity data
   * @param {string} sapTable - Target SAP table
   * @returns {string} CSV content
   */
  exportCSV(data, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) return '';

    const delimiter = this.config.file.delimiter || ',';
    const reverseMap = {};
    Object.entries(tableConfig.fields).forEach(([sapField, mapping]) => {
      reverseMap[mapping.target] = sapField;
    });

    // Header row — SAP field names
    const sapFields = Object.keys(tableConfig.fields);
    const header = sapFields.map(f => `"${f}"`).join(delimiter);

    // Data rows
    const rows = data.map(item => {
      return sapFields.map(sapField => {
        const localField = tableConfig.fields[sapField].target;
        const value = item[localField] ?? '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(delimiter);
    });

    this._logSync('export', sapTable, data.length, 'csv');
    return [header, ...rows].join('\n');
  }

  /**
   * Export local data to SAP IDoc XML format
   * @param {Array} data - Local entity data
   * @param {string} sapTable - Target SAP table
   * @returns {string} IDoc XML content
   */
  exportIDoc(data, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) return '';

    const segmentName = this._tableToIdocSegment(sapTable);
    const timestamp = new Date().toISOString().replace(/[-:T]/g, '').substring(0, 14);

    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<IDOC BEGIN="1">\n`;
    xml += `  <EDI_DC40 SEGMENT="1">\n`;
    xml += `    <TABNAM>EDI_DC40</TABNAM>\n`;
    xml += `    <DIRECT>2</DIRECT>\n`;
    xml += `    <IDOCTYP>OPTIMAL_EXPORT</IDOCTYP>\n`;
    xml += `    <MESTYP>${sapTable}_EXPORT</MESTYP>\n`;
    xml += `    <SNDPOR>OPTIMAL</SNDPOR>\n`;
    xml += `    <SNDPRT>LS</SNDPRT>\n`;
    xml += `    <SNDPRN>OPTIMAL_STUDIO</SNDPRN>\n`;
    xml += `    <RCVPOR>SAP</RCVPOR>\n`;
    xml += `    <RCVPRT>LS</RCVPRT>\n`;
    xml += `    <RCVPRN>SAP_PM</RCVPRN>\n`;
    xml += `    <CREDAT>${timestamp.substring(0, 8)}</CREDAT>\n`;
    xml += `    <CRETIM>${timestamp.substring(8)}</CRETIM>\n`;
    xml += `  </EDI_DC40>\n`;

    data.forEach((item, idx) => {
      xml += `  <${segmentName} SEGMENT="1">\n`;
      Object.entries(tableConfig.fields).forEach(([sapField, mapping]) => {
        const value = item[mapping.target] ?? '';
        xml += `    <${sapField}>${this._escapeXml(String(value))}</${sapField}>\n`;
      });
      xml += `  </${segmentName}>\n`;
    });

    xml += `</IDOC>`;

    this._logSync('export', sapTable, data.length, 'idoc');
    return xml;
  }

  /**
   * Export local data to JSON (SAP OData compatible format)
   * @param {Array} data - Local entity data
   * @param {string} sapTable - Target SAP table
   * @returns {Object} JSON in OData response format
   */
  exportJSON(data, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) return { d: { results: [] } };

    const results = data.map(item => {
      const record = {};
      Object.entries(tableConfig.fields).forEach(([sapField, mapping]) => {
        record[sapField] = item[mapping.target] ?? '';
      });
      return record;
    });

    this._logSync('export', sapTable, data.length, 'json');

    return {
      d: {
        results,
        __count: String(results.length)
      }
    };
  }

  // ========== FIELD MAPPING ==========

  /**
   * Get the field mapping for a specific SAP table
   */
  getFieldMapping(sapTable) {
    const config = this.fieldMappings[sapTable];
    if (!config) return null;

    return {
      table: sapTable,
      entity: config.entity,
      fields: Object.entries(config.fields).map(([sapField, mapping]) => ({
        sapField,
        localField: mapping.target,
        label: mapping.label,
        override: this.config.mappingOverrides?.[sapTable]?.[sapField] || null
      }))
    };
  }

  /**
   * Set a custom field mapping override
   */
  setFieldMappingOverride(sapTable, sapField, localField) {
    if (!this.config.mappingOverrides[sapTable]) {
      this.config.mappingOverrides[sapTable] = {};
    }
    this.config.mappingOverrides[sapTable][sapField] = localField;
  }

  /**
   * Merge imported SAP data with existing project data
   * @param {Object} projectData - Current project data
   * @param {Array} importedRecords - Records from SAP import
   * @param {string} entity - Target entity type
   * @param {string} mergeMode - 'replace' | 'merge' | 'append'
   * @returns {Object} Updated project data
   */
  mergeData(projectData, importedRecords, entity, mergeMode = 'merge') {
    const updatedData = { ...projectData };
    const existing = updatedData[entity] || [];

    switch (mergeMode) {
      case 'replace':
        updatedData[entity] = importedRecords;
        break;

      case 'append':
        updatedData[entity] = [...existing, ...importedRecords];
        break;

      case 'merge':
      default: {
        const existingMap = new Map(existing.map(item => [item.id, item]));
        importedRecords.forEach(record => {
          if (existingMap.has(record.id)) {
            // Merge fields — SAP data overwrites matching fields
            const merged = { ...existingMap.get(record.id), ...record };
            existingMap.set(record.id, merged);
          } else {
            existingMap.set(record.id, record);
          }
        });
        updatedData[entity] = Array.from(existingMap.values());
        break;
      }
    }

    return updatedData;
  }

  // ========== SYNC LOG ==========

  getSyncLog() {
    return [...this.syncLog];
  }

  clearSyncLog() {
    this.syncLog = [];
  }

  getLastSync() {
    return this.syncLog.length > 0 ? this.syncLog[this.syncLog.length - 1] : null;
  }

  // ========== VALIDATION ==========

  /**
   * Validate imported data against SAP field rules
   */
  validateImport(records, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) return { valid: false, errors: ['Unknown table'] };

    const errors = [];
    const warnings = [];

    records.forEach((record, idx) => {
      // Check required fields
      if (!record.id && !record.name) {
        errors.push(`Row ${idx + 1}: Missing both ID and Name fields`);
      }

      // Check date formats
      Object.entries(record).forEach(([key, value]) => {
        if (key.includes('Date') || key.includes('date')) {
          if (value && !/^\d{4}-?\d{2}-?\d{2}/.test(String(value))) {
            warnings.push(`Row ${idx + 1}: Field "${key}" has non-standard date format: ${value}`);
          }
        }
      });

      // Check numeric fields
      if (record.capacity && isNaN(parseFloat(record.capacity))) {
        warnings.push(`Row ${idx + 1}: Non-numeric capacity value: ${record.capacity}`);
      }
      if (record.downtime && isNaN(parseFloat(record.downtime))) {
        warnings.push(`Row ${idx + 1}: Non-numeric downtime value: ${record.downtime}`);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      recordCount: records.length
    };
  }

  /**
   * Generate a data quality report for the import
   */
  generateDataQualityReport(records, sapTable) {
    const tableConfig = this.fieldMappings[sapTable];
    if (!tableConfig) return null;

    const fieldCoverage = {};
    const expectedFields = Object.values(tableConfig.fields).map(f => f.target);

    expectedFields.forEach(field => {
      const filled = records.filter(r => r[field] !== undefined && r[field] !== '').length;
      fieldCoverage[field] = {
        filled,
        total: records.length,
        percentage: records.length > 0 ? ((filled / records.length) * 100).toFixed(1) : 0
      };
    });

    const overallCompleteness = expectedFields.reduce((sum, field) => {
      return sum + (parseFloat(fieldCoverage[field].percentage) || 0);
    }, 0) / expectedFields.length;

    return {
      table: sapTable,
      entity: tableConfig.entity,
      totalRecords: records.length,
      fieldCoverage,
      overallCompleteness: overallCompleteness.toFixed(1),
      duplicateIds: this._findDuplicates(records.map(r => r.id)),
      missingIds: records.filter(r => !r.id).length
    };
  }

  // ========== PRIVATE HELPERS ==========

  _parseCSVLine(line, delimiter) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    result.push(current.trim());
    return result;
  }

  _parseValue(value, fieldName) {
    if (value === undefined || value === null) return '';
    const str = String(value).trim().replace(/^"|"$/g, '');

    // Parse SAP date format (YYYYMMDD)
    if (/^\d{8}$/.test(str) && (fieldName.includes('DAT') || fieldName.includes('DT') || fieldName.includes('MN'))) {
      return `${str.substring(0, 4)}-${str.substring(4, 6)}-${str.substring(6, 8)}`;
    }

    // Parse numbers
    if (/^-?\d+\.?\d*$/.test(str) && !fieldName.includes('NR') && !fieldName.includes('NUM')) {
      return str;
    }

    return str;
  }

  _fuzzyMatchField(input, fieldMap) {
    const inputUpper = input.toUpperCase().replace(/[_\s-]/g, '');

    for (const [sapField, mapping] of Object.entries(fieldMap)) {
      const fieldUpper = sapField.toUpperCase().replace(/[_\s-]/g, '');
      const labelUpper = mapping.label.toUpperCase().replace(/[_\s-]/g, '');

      if (inputUpper === fieldUpper || inputUpper === labelUpper) return mapping;
      if (inputUpper.includes(fieldUpper) || fieldUpper.includes(inputUpper)) return mapping;
      if (inputUpper.includes(labelUpper) || labelUpper.includes(inputUpper)) return mapping;
    }
    return null;
  }

  _detectTableFromFields(record) {
    if (!record || typeof record !== 'object') return null;

    const fields = Object.keys(record);
    let bestMatch = null;
    let bestScore = 0;

    for (const [table, config] of Object.entries(this.fieldMappings)) {
      const sapFields = Object.keys(config.fields);
      const matches = fields.filter(f => sapFields.includes(f)).length;
      if (matches > bestScore) {
        bestScore = matches;
        bestMatch = table;
      }
    }

    return bestScore >= 2 ? bestMatch : null;
  }

  _detectTableFromCSVHeaders(headerLine) {
    const headers = headerLine.split(/[,;\t]/).map(h => h.trim().replace(/^"|"$/g, ''));
    return this._detectTableFromFields(
      Object.fromEntries(headers.map(h => [h, true]))
    );
  }

  _idocSegmentToTable(segmentName) {
    const map = {
      E1EQUI: 'EQUI', E1EQUI_CREATE: 'EQUI', E1EQUI_CHANGE: 'EQUI',
      E1IFLO: 'IFLO', E1IFLO_CREATE: 'IFLO',
      E1QMEL: 'QMEL', E1BSEG_NOTIFICATION: 'QMEL',
      E1AUFK: 'AUFK', E1BSEG_ORDER: 'AUFK',
      E1PLKO: 'PLKO', E1STPO: 'STPO', E1MPLA: 'MPLA'
    };
    return map[segmentName] || null;
  }

  _tableToIdocSegment(sapTable) {
    const map = {
      EQUI: 'E1EQUI', IFLO: 'E1IFLO', QMEL: 'E1QMEL',
      AUFK: 'E1AUFK', PLKO: 'E1PLKO', STPO: 'E1STPO', MPLA: 'E1MPLA'
    };
    return map[sapTable] || `E1${sapTable}`;
  }

  _escapeXml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  _logSync(direction, table, recordCount, format) {
    this.syncLog.push({
      timestamp: new Date().toISOString(),
      direction,
      table,
      recordCount,
      format,
      status: 'success'
    });
  }

  _findDuplicates(arr) {
    const seen = new Set();
    const dupes = new Set();
    arr.forEach(item => {
      if (item && seen.has(item)) dupes.add(item);
      if (item) seen.add(item);
    });
    return Array.from(dupes);
  }

  // ========== SAP BW/BI INTEGRATION ==========

  /**
   * SAP BW InfoProvider query mapping
   * Maps common BW reporting cubes to analytics queries
   */
  static BW_INFOPROVIDERS = {
    'PM_NOTIFICATION': {
      description: 'Plant Maintenance Notifications',
      keyFigures: ['BREAKDOWN_DURATION', 'NOTIFICATION_COUNT', 'MTBF', 'MTTR'],
      dimensions: ['EQUIPMENT', 'FUNCTIONAL_LOCATION', 'NOTIFICATION_TYPE', 'PRIORITY', 'TIME']
    },
    'PM_ORDER': {
      description: 'Plant Maintenance Orders',
      keyFigures: ['ORDER_COST', 'PLANNED_COST', 'ACTUAL_HOURS', 'PLANNED_HOURS', 'ORDER_COUNT'],
      dimensions: ['EQUIPMENT', 'ORDER_TYPE', 'WORK_CENTER', 'COST_CENTER', 'TIME']
    },
    'PM_CONFIRMATION': {
      description: 'Maintenance Confirmations',
      keyFigures: ['ACTUAL_WORK', 'ACTUAL_DURATION', 'VARIANCE'],
      dimensions: ['ORDER', 'OPERATION', 'WORK_CENTER', 'TIME']
    },
    'PM_MEASURING_POINT': {
      description: 'Measuring Points & Counters',
      keyFigures: ['MEASUREMENT_VALUE', 'COUNTER_READING'],
      dimensions: ['EQUIPMENT', 'MEASURING_POINT', 'TIME']
    },
    'EQUIPMENT_HISTORY': {
      description: 'Equipment Lifecycle History',
      keyFigures: ['AVAILABILITY', 'RELIABILITY', 'FAILURE_COUNT', 'DOWNTIME_HOURS'],
      dimensions: ['EQUIPMENT', 'FUNCTIONAL_LOCATION', 'PLANT', 'TIME']
    }
  };

  /**
   * Import SAP BW query results (typically exported as CSV or JSON)
   * @param {string|Object} data - BW query export data
   * @param {string} infoProvider - BW InfoProvider name
   * @returns {Object} Parsed analytics data
   */
  importBWData(data, infoProvider) {
    const providerConfig = SAPConnector.BW_INFOPROVIDERS[infoProvider];
    if (!providerConfig) {
      return { success: false, error: `Unknown InfoProvider: ${infoProvider}`, data: [] };
    }

    let records;
    if (typeof data === 'string') {
      // Try JSON first, then CSV
      try {
        const json = JSON.parse(data);
        records = json.d?.results || json.value || json.results || json;
      } catch (e) {
        // Parse as CSV
        const lines = data.trim().split('\n');
        const headers = this._parseCSVLine(lines[0], this.config.file.delimiter || ',');
        records = [];
        for (let i = 1; i < lines.length; i++) {
          const values = this._parseCSVLine(lines[i], this.config.file.delimiter || ',');
          const record = {};
          headers.forEach((h, idx) => {
            record[h.trim().replace(/^"|"$/g, '')] = values[idx]?.trim()?.replace(/^"|"$/g, '');
          });
          records.push(record);
        }
      }
    } else {
      records = Array.isArray(data) ? data : [data];
    }

    if (!Array.isArray(records)) {
      return { success: false, error: 'Could not parse BW data', data: [] };
    }

    this._logSync('import', `BW:${infoProvider}`, records.length, 'bw');

    return {
      success: true,
      infoProvider,
      description: providerConfig.description,
      records,
      recordCount: records.length,
      keyFigures: providerConfig.keyFigures,
      dimensions: providerConfig.dimensions
    };
  }

  /**
   * Calculate KPIs from BW notification data
   * @param {Array} notificationRecords - Records from PM_NOTIFICATION InfoProvider
   * @returns {Object} Calculated reliability KPIs
   */
  calculateBWKPIs(notificationRecords) {
    if (!notificationRecords || notificationRecords.length === 0) {
      return { mtbf: 0, mttr: 0, availability: 0, failureCount: 0, totalDowntime: 0 };
    }

    const byEquipment = {};
    notificationRecords.forEach(r => {
      const equip = r.EQUIPMENT || r.EQUNR || r.equipmentId || 'UNKNOWN';
      if (!byEquipment[equip]) byEquipment[equip] = [];
      byEquipment[equip].push(r);
    });

    let totalFailures = 0;
    let totalDowntime = 0;
    let totalOperatingTime = 0;
    const equipmentCount = Object.keys(byEquipment).length;

    Object.entries(byEquipment).forEach(([equip, records]) => {
      const failures = records.length;
      totalFailures += failures;

      const downtime = records.reduce((sum, r) => {
        return sum + (parseFloat(r.BREAKDOWN_DURATION || r.AUSZT || r.downtime) || 0);
      }, 0);
      totalDowntime += downtime;

      // Assume 8760 hours/year operating time per equipment
      totalOperatingTime += 8760;
    });

    const mtbf = totalFailures > 0 ? (totalOperatingTime - totalDowntime) / totalFailures : totalOperatingTime;
    const mttr = totalFailures > 0 ? totalDowntime / totalFailures : 0;
    const availability = totalOperatingTime > 0 ? ((totalOperatingTime - totalDowntime) / totalOperatingTime) * 100 : 100;

    return {
      mtbf: parseFloat(mtbf.toFixed(2)),
      mttr: parseFloat(mttr.toFixed(2)),
      availability: parseFloat(availability.toFixed(4)),
      failureCount: totalFailures,
      totalDowntime: parseFloat(totalDowntime.toFixed(2)),
      equipmentCount,
      failureRate: totalFailures > 0 ? parseFloat((totalFailures / totalOperatingTime).toFixed(8)) : 0
    };
  }

  /**
   * Generate SAP BW analytics report data
   * @param {Array} records - BW query records
   * @param {string} groupBy - Dimension to group by
   * @param {string} keyFigure - Key figure to aggregate
   * @returns {Array} Aggregated report data
   */
  aggregateBWData(records, groupBy, keyFigure) {
    const groups = {};
    records.forEach(r => {
      const key = r[groupBy] || 'Other';
      if (!groups[key]) groups[key] = { count: 0, sum: 0, min: Infinity, max: -Infinity, values: [] };
      const val = parseFloat(r[keyFigure]) || 0;
      groups[key].count++;
      groups[key].sum += val;
      groups[key].min = Math.min(groups[key].min, val);
      groups[key].max = Math.max(groups[key].max, val);
      groups[key].values.push(val);
    });

    return Object.entries(groups).map(([key, stats]) => ({
      [groupBy]: key,
      count: stats.count,
      sum: parseFloat(stats.sum.toFixed(2)),
      average: parseFloat((stats.sum / stats.count).toFixed(2)),
      min: stats.min === Infinity ? 0 : stats.min,
      max: stats.max === -Infinity ? 0 : stats.max
    }));
  }

  /**
   * Get available BW InfoProviders
   */
  getBWInfoProviders() {
    return Object.entries(SAPConnector.BW_INFOPROVIDERS).map(([id, config]) => ({
      id,
      ...config
    }));
  }

  /**
   * Calculate Time-to-Failure (TTF) from maintenance order dates
   * @param {Array} maintenanceOrders - Array of maintenance orders with dates
   * @returns {Object} TTF statistics { meanTTF, minTTF, maxTTF, orders: [] }
   */
  calculateTTF(maintenanceOrders) {
    if (!Array.isArray(maintenanceOrders) || maintenanceOrders.length === 0) {
      return { meanTTF: 0, minTTF: 0, maxTTF: 0, orders: [] };
    }

    const ttfData = [];
    let totalTTF = 0;

    for (let i = 1; i < maintenanceOrders.length; i++) {
      const prevOrder = maintenanceOrders[i - 1];
      const currOrder = maintenanceOrders[i];

      const prevDate = new Date(prevOrder.actualEnd || prevOrder.scheduledEnd);
      const currDate = new Date(currOrder.actualStart || currOrder.scheduledStart);

      if (!isNaN(prevDate) && !isNaN(currDate) && currDate > prevDate) {
        const ttfHours = (currDate - prevDate) / (1000 * 60 * 60);
        ttfData.push({
          orderId: currOrder.id,
          previousOrderId: prevOrder.id,
          ttfHours: parseFloat(ttfHours.toFixed(2)),
          ttfDays: parseFloat((ttfHours / 24).toFixed(2))
        });
        totalTTF += ttfHours;
      }
    }

    const meanTTF = ttfData.length > 0 ? totalTTF / ttfData.length : 0;
    const ttfValues = ttfData.map(d => d.ttfHours);
    const minTTF = ttfValues.length > 0 ? Math.min(...ttfValues) : 0;
    const maxTTF = ttfValues.length > 0 ? Math.max(...ttfValues) : 0;

    return {
      meanTTF: parseFloat(meanTTF.toFixed(2)),
      minTTF: parseFloat(minTTF.toFixed(2)),
      maxTTF: parseFloat(maxTTF.toFixed(2)),
      ordersAnalyzed: ttfData.length,
      orders: ttfData
    };
  }

  /**
   * Calculate Performance KPIs (MTBF, MTTR, Availability, OEE) from maintenance orders
   * @param {Array} orders - Array of maintenance orders
   * @returns {Object} KPI metrics { MTBF, MTTR, availability, OEE }
   */
  calculatePerformanceKPIs(orders) {
    if (!Array.isArray(orders) || orders.length === 0) {
      return {
        MTBF: 0,
        MTTR: 0,
        availability: 100,
        OEE: 100,
        failureCount: 0,
        totalDowntime: 0,
        totalUptime: 0
      };
    }

    let totalDowntime = 0;
    let totalUptime = 0;
    let failureCount = 0;

    orders.forEach(order => {
      // Count corrective (failure) orders vs preventive
      if (order.orderType === 'C' || order.orderType === 'CORR') {
        failureCount++;

        // Calculate downtime from order duration
        if (order.actualStart && order.actualEnd) {
          const startDate = new Date(order.actualStart);
          const endDate = new Date(order.actualEnd);
          if (!isNaN(startDate) && !isNaN(endDate)) {
            const downtime = (endDate - startDate) / (1000 * 60 * 60);
            totalDowntime += downtime;
          }
        } else if (order.plannedWork) {
          totalDowntime += order.plannedWork;
        }
      }

      // Estimate uptime from interval to next failure
      if (order.actualEnd) {
        totalUptime += 8760; // Assume 1 year per order (8760 hours)
      }
    });

    const mtbf = failureCount > 0 ? totalUptime / failureCount : totalUptime;
    const mttr = failureCount > 0 ? totalDowntime / failureCount : 0;
    const availability = totalUptime > 0 ? ((totalUptime - totalDowntime) / totalUptime) * 100 : 100;

    // OEE = Availability × Performance × Quality
    // Simplified: OEE = Availability (conservative estimate)
    const oee = Math.min(availability, 100);

    return {
      MTBF: parseFloat(mtbf.toFixed(2)),
      MTTR: parseFloat(mttr.toFixed(2)),
      availability: parseFloat(availability.toFixed(2)),
      OEE: parseFloat(oee.toFixed(2)),
      failureCount,
      totalDowntime: parseFloat(totalDowntime.toFixed(2)),
      totalUptime: parseFloat(totalUptime.toFixed(2)),
      failureRate: failureCount > 0 ? parseFloat((failureCount / totalUptime).toFixed(6)) : 0
    };
  }
}

/**
 * SAP Write-Back Handler — converts OptimalAvailability data to SAP formats
 * Handles bi-directional sync for maintenance orders, notifications, task lists, and BOMs
 */
class SAPWriteBack {
  /**
   * Convert OptimalAvailability scheduled/corrective task to SAP PM order format (IDoc XML)
   * @param {Object} taskData - Task object with id, description, type, scheduledStart, etc.
   * @returns {Object} SAP PM order in IDoc format { AUFNR, AUART, KTEXT, GSTRP, GLTRP, ... }
   */
  static generateMaintenanceOrder(taskData) {
    return {
      AUFNR: taskData.id || `ORDER-${Date.now()}`,
      AUART: taskData.type === 'preventive' ? 'PM' : 'ZM', // PM = Preventive, ZM = Corrective
      KTEXT: (taskData.description || '').substring(0, 40), // Max 40 chars for SAP
      EQUNR: taskData.equipmentId || '',
      TPLNR: taskData.locationId || '',
      GSTRP: this._formatSAPDate(taskData.scheduledStart),
      GLTRP: this._formatSAPDate(taskData.scheduledEnd),
      KOSTL: taskData.costCenter || '',
      ARBEI: taskData.plannedWork || 0,
      PRIOK: this._mapPriorityToSAP(taskData.priority),
      WERKS: taskData.plantCode || '',
      INGRP: taskData.maintenanceGroup || '',
      STABU: taskData.status === 'completed' ? 'TECO' : 'CRTD' // Create/Complete status
    };
  }

  /**
   * Convert failure event to SAP notification format (QMEL table)
   * @param {Object} failureEvent - Event with breakdownStart, downtime, equipmentId, etc.
   * @returns {Object} SAP notification record
   */
  static generateNotification(failureEvent) {
    return {
      QMNUM: failureEvent.id || `NOTIF-${Date.now()}`,
      QMART: failureEvent.notificationType || '01', // 01 = Breakdown
      QMTXT: (failureEvent.description || '').substring(0, 40),
      EQUNR: failureEvent.equipmentId || '',
      TPLNR: failureEvent.locationId || '',
      AUSVN: this._formatSAPDate(failureEvent.breakdownStart),
      AUSBS: this._formatSAPDate(failureEvent.breakdownEnd),
      AUSZT: failureEvent.downtime || 0,
      PRIESSION: this._mapPriorityToSAP(failureEvent.priority),
      FEGRP: failureEvent.failureCodeGroup || '',
      FECOD: failureEvent.failureCode || '',
      OTGRP: failureEvent.causeCodeGroup || '',
      OTEIL: failureEvent.causeCode || '',
      UESSION: failureEvent.damageCodeGroup || '',
      URGRP: failureEvent.damageCode || ''
    };
  }

  /**
   * Convert RCM task recommendations to SAP task list format (PLKO)
   * @param {Array} rcmTasks - Array of RCM tasks with description, interval, taskType, etc.
   * @returns {Array} SAP task list records
   */
  static generateTaskList(rcmTasks) {
    return rcmTasks.map((task, idx) => ({
      PLNTY: 'N', // Task list type
      PLNNR: task.id || `TASKLIST-${Date.now()}-${idx}`,
      PLNAL: '1', // Counter
      KTEXT: (task.description || '').substring(0, 40),
      WERKS: task.plantCode || '',
      VERWE: this._mapTaskTypeToSAPUsage(task.taskType),
      STATU: 'AS', // Released status
      VAESSION: this._formatSAPDate(task.validFrom || new Date()),
      DELKZ: '0' // Not marked for deletion
    }));
  }

  /**
   * Convert BOM data to SAP BOM format (STPO table)
   * @param {Object} bomData - BOM with bomId, items array containing id, quantity, unit
   * @returns {Array} SAP BOM line items
   */
  static exportBOMToSAP(bomData) {
    if (!bomData || !bomData.items || bomData.items.length === 0) {
      return [];
    }

    return bomData.items.map((item, idx) => ({
      STLNR: bomData.bomId || `BOM-${Date.now()}`,
      POSNR: String(idx + 1).padStart(4, '0'), // Item number
      IDNRK: item.id || '',
      MENGE: item.quantity || 1,
      MEINS: item.unit || 'EA', // Unit of measure
      POTX1: (item.description || '').substring(0, 40),
      POSTP: item.itemCategory || 'L' // L = Material
    }));
  }

  /**
   * Create bi-directional sync payload for batch transfer
   * @param {Array} entities - Array of entities to sync
   * @param {string} direction - 'import' or 'export'
   * @returns {Object} Sync payload with metadata and records
   */
  static generateSyncPayload(entities, direction = 'export') {
    return {
      syncMetadata: {
        timestamp: new Date().toISOString(),
        direction: direction,
        entityCount: entities.length,
        syncId: `SYNC-${Date.now()}`,
        sourceSystem: 'OptimalAvailability',
        targetSystem: 'SAP'
      },
      data: entities,
      conflictResolution: 'sap-wins', // Conflict strategy
      validation: {
        enabled: true,
        strict: false
      }
    };
  }

  // ========== PRIVATE HELPERS ==========

  /**
   * Format date to SAP format (YYYYMMDD)
   * @private
   */
  static _formatSAPDate(dateValue) {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d)) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  /**
   * Map priority value to SAP priority code
   * @private
   */
  static _mapPriorityToSAP(priority) {
    const mapping = {
      'critical': '1', 'high': '2', 'medium': '3', 'low': '4',
      1: '1', 2: '2', 3: '3', 4: '4'
    };
    return mapping[priority] || '3';
  }

  /**
   * Map task type to SAP task list usage
   * @private
   */
  static _mapTaskTypeToSAPUsage(taskType) {
    const mapping = {
      'preventive': 'PM', 'corrective': 'Z1',
      'inspection': 'Z2', 'modification': 'Z3'
    };
    return mapping[taskType?.toLowerCase()] || 'PM';
  }
}

/**
 * SAP Live Connector — OData/RFC API connection for real-time synchronization
 * Connects to SAP S/4HANA or ECC via REST OData endpoints
 */
class SAPLiveConnector {
  constructor(config) {
    this.baseUrl = config.baseUrl || '';
    this.client = config.client || '100';
    this.credentials = config.credentials || {};
    this.connected = false;
  }

  async testConnection() {
    try {
      const response = await fetch(`${this.baseUrl}API_EQUIPMENT/A_Equipment?$top=1&sap-client=${this.client}`, {
        headers: this._getHeaders(),
        signal: AbortSignal.timeout(10000)
      });
      this.connected = response.ok;
      return { success: response.ok, status: response.status, message: response.ok ? 'Connected to SAP' : `HTTP ${response.status}` };
    } catch (err) {
      return { success: false, status: 0, message: err.message };
    }
  }

  _getHeaders() {
    const headers = { 'Accept': 'application/json', 'X-CSRF-Token': 'Fetch' };
    if (this.credentials.token) {
      headers['Authorization'] = `Bearer ${this.credentials.token}`;
    } else if (this.credentials.username) {
      headers['Authorization'] = 'Basic ' + btoa(`${this.credentials.username}:${this.credentials.password}`);
    }
    return headers;
  }

  async fetchEquipment(filters = {}) {
    const params = new URLSearchParams({ 'sap-client': this.client, '$format': 'json' });
    if (filters.plant) params.append('$filter', `MaintenancePlant eq '${filters.plant}'`);
    const response = await fetch(`${this.baseUrl}API_EQUIPMENT/A_Equipment?${params}`, { headers: this._getHeaders() });
    if (!response.ok) throw new Error(`SAP API error: ${response.status}`);
    const data = await response.json();
    return (data.d?.results || []).map(item => SAPConnector._mapSAPRecord(item, SAP_TABLE_MAP.EQUI));
  }

  async fetchNotifications(filters = {}) {
    const params = new URLSearchParams({ 'sap-client': this.client, '$format': 'json' });
    if (filters.dateFrom) params.append('$filter', `CreationDate ge datetime'${filters.dateFrom}'`);
    const response = await fetch(`${this.baseUrl}API_MAINTNOTIFICATION/A_MaintenanceNotification?${params}`, { headers: this._getHeaders() });
    if (!response.ok) throw new Error(`SAP API error: ${response.status}`);
    const data = await response.json();
    return (data.d?.results || []).map(item => SAPConnector._mapSAPRecord(item, SAP_TABLE_MAP.QMEL));
  }

  async fetchMaintenanceOrders(filters = {}) {
    const params = new URLSearchParams({ 'sap-client': this.client, '$format': 'json' });
    const response = await fetch(`${this.baseUrl}API_MAINTENANCEORDER/A_MaintenanceOrder?${params}`, { headers: this._getHeaders() });
    if (!response.ok) throw new Error(`SAP API error: ${response.status}`);
    const data = await response.json();
    return (data.d?.results || []).map(item => SAPConnector._mapSAPRecord(item, SAP_TABLE_MAP.AUFK));
  }

  async pushWorkOrder(orderData) {
    const response = await fetch(`${this.baseUrl}API_MAINTENANCEORDER/A_MaintenanceOrder`, {
      method: 'POST',
      headers: { ...this._getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(SAPWriteBack.generateMaintenanceOrder(orderData))
    });
    if (!response.ok) throw new Error(`SAP POST error: ${response.status}`);
    return await response.json();
  }
}

// Static reference
SAPConnector.TABLE_MAP = SAP_TABLE_MAP;
SAPConnector.DEFAULT_CONFIG = DEFAULT_SAP_CONFIG;

export default SAPConnector;
export { SAPWriteBack, SAPLiveConnector };
